import {type Map as ImmutableMap} from 'immutable';
import {lruMemoize} from 'reselect';
import {isEqual} from 'lodash';
import {reportBug} from '@packages/utilities/reporter';
import {weakMemo} from '@packages/utilities/memo';

import type {
  StyleBlockId,
  StyleBlock,
} from '@packages/systems/style-legacy/records';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  getTagFromData,
  getSplitElementWithAncestors,
  getDescendants,
} from '@packages/utilities/platform';
import {getStyleBlockIds} from '@packages/systems/style/style-blocks';

import {isStringElement} from '@packages/utilities/elementTypeUtils';
import {TAG_BY_NODE_TYPE} from '@packages/systems/core/constants/TagToTypeMigration';
import {getName} from '@packages/systems/wfdl-Qualified';
import {Component, Expressions, Plugins} from '@packages/systems/wfdl';
import {VariantDataMap} from '@packages/systems/style-legacy/selectors';
import {toCssClass} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockUtils';

export const calculateAffectedForStyleBlock = ({
  componentMap,
  plugins,
  styleBlockState,
  styleBlockId,
}: {
  componentMap: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  styleBlockState: StyleBlockStateType;
  styleBlockId: StyleBlockId | null;
}): ReadonlyArray<string> => {
  if (styleBlockId == null) return [];

  const affectedElementsById = recalculateAffected_fast(
    componentMap,
    plugins,
    getStyleBlockLookups(
      styleBlockState.styleBlocks,
      Plugins.getPluginsSlugMap(plugins)
    )
  );
  const affectedElements = affectedElementsById[styleBlockId];
  return affectedElements ?? [];
};

export const calculateAffectedForElement = ({
  expression,
  styleBlockState,
  elementId,
  slugMap,
}: {
  expression: Expression;
  styleBlockState: StyleBlockStateType;
  elementId: string;
  slugMap: Plugins.PluginsType['slugMap'];
}): Set<StyleBlockId> => {
  const [ancestors, element] =
    getSplitElementWithAncestors(expression)(elementId);

  if (!element) return new Set();

  const applies_mut = new Set<string>();

  _addStyleBlocksAppliedToElement(
    getStyleBlockLookups(styleBlockState.styleBlocks, slugMap),
    element,
    ancestors,
    applies_mut
  );

  return applies_mut;
};

/**
 * Computes style blocks that apply along an ordered ancestor path.
 *
 * Returns:
 * - `blockGuidsAffectingAncestors`: ordered list of affecting block ids
 * - `blockGuidsDistanceById`: block id -> distance from the path tail
 *   (if a block appears multiple times, the latest distance is kept)
 */
export const calculateAffectedForAncestorPath = ({
  expression,
  styleBlockState,
  ancestorPath,
  slugMap,
}: {
  expression: Expression;
  styleBlockState: StyleBlockStateType;
  ancestorPath: string[];
  slugMap: Plugins.PluginsType['slugMap'];
}): {
  blockGuidsAffectingAncestors: string[];
  blockGuidsDistanceById: {[key: string]: number};
} => {
  const lookups = getStyleBlockLookups(styleBlockState.styleBlocks, slugMap);
  const ancestorStack: ExpressionElement[] = [];
  const blockGuidsAffectingAncestors: string[] = [];
  const blockGuidsDistanceById: {[key: string]: number} = {};
  const pathLength = ancestorPath.length;

  ancestorPath.forEach((elementId, index) => {
    const element = Expressions.getElementById(expression, elementId);

    if (!element) return;

    const applies_mut = new Set<string>();
    _addStyleBlocksAppliedToElement(
      lookups,
      element,
      ancestorStack,
      applies_mut
    );

    for (const block of applies_mut) {
      blockGuidsAffectingAncestors.push(block);
      blockGuidsDistanceById[block] = pathLength - index;
    }

    ancestorStack.push(element);
  });

  return {blockGuidsAffectingAncestors, blockGuidsDistanceById};
};

/**
 * Helper function for `recalculateAffected_fast`
 *
 * Produces a `Set` of "cssified" class names, making use of pre-computed data
 * from `FastStyleBlock`s.
 */
const getClassNameSet = (
  styleBlocks: Readonly<Map<StyleBlockId, FastStyleBlock>>,
  styleBlockIds: Iterable<string>
) => {
  const classNames = new Set<string>();

  for (const styleBlockId of styleBlockIds) {
    const styleBlock = styleBlocks.get(styleBlockId);
    if (!styleBlock) continue;
    const className = styleBlock.name;
    classNames.add(className);
  }

  return classNames;
};

/**
 * In `recalculateAffected_fast`, we do a _lot_ of reads and lookups involving
 * style blocks since their data isn't readily useful for several things.
 *
 * This class helps accelerate these things by storing some pre-computed data
 * that isn't available on normal `StyleBlock` instances, namely:
 *
 * - `parent` - a pointer to the parent block (if any)
 * - `name` - the "cssified" class name (or tag)
 *
 * For convenience we also store the other relevant style block data so we don't
 * have to look them up separately.
 */
interface FastStyleBlock {
  readonly id: string;
  readonly type: 'class' | 'tag';
  readonly name: string;
  readonly comb: '' | '&' | '|';
  readonly parent: null | FastStyleBlock;
}

class FastStyleBlockImpl implements FastStyleBlock {
  id: string;
  type: 'class' | 'tag';
  name: string;
  comb: '' | '&' | '|';
  parent: null | FastStyleBlock;
  constructor(
    id: string,
    type: 'class' | 'tag' | 'element',
    name: string,
    comb: '' | '&' | '|'
  ) {
    this.id = id;
    // TODO(lou): We can probably track element blocks directly which should
    // improve performance, but for now trying to keep our existing logic as-is
    // by coding element styles as classes.
    //
    // Tracking: https://webflow.atlassian.net/browse/STYL-2047
    this.type = type === 'element' ? 'class' : type;
    this.name = name;
    this.comb = comb;
    this.parent = null;
  }
}

/**
 * Diff two immutable maps of `StyleBlock`s purely by their selector
 * structure, ignoring differences in the style data itself.
 *
 * Returns the ids of blocks whose only selector-relevant change is `name`
 * (empty when the maps are equivalent), or `null` when the selector structure
 * differs in any other way. Renamed ids are patched into the cached maps by
 * `renameInStyleBlockLookups` instead of forcing a rebuild.
 *
 * This allows us to reuse previously-computed hash maps during common
 * operations that change styles but don't add or remove any new style blocks.
 *
 * For example, suppose we have these two maps, which are identical except for
 * the `color:` style property set on the `block-foo` style block:
 *
 * ```js
 * nextStyleBlocks = {
 *   'block-foo': {
 *     type: 'class',
 *     name: 'foo'
 *     children: [],
 *     comb: '',
 *     styleLess: 'color: red;',
 *     variants: {...},
 *   },
 * }
 *
 * prevStyleBlocks = {
 *   'block-foo': {
 *     type: 'class',
 *     name: 'foo'
 *     children: [],
 *     comb: '',
 *     styleLess: 'color: blue;',
 *     variants: {...},
 *   },
 * }
 * ```
 *
 * These would be treated as equivalent (an empty result), because we ignore
 * `styleLess` and `variants` which have no impact on the selector structure.
 *
 * This function is a `O(N)` comparison, whereas constructing our maps is
 * `O(k*N)`, where `k` refers to many small operations like map inserts,
 * lookups, and string processings that happen in `getStyleBlockLookups`.
 *
 * In theory, this doesn't impact the big-O performance profile of
 * `recalculateAffected_fast` because it's already `O(k*N)`, so this is just
 * adding another `N` which gets "absorbed" by the constant `k` multiple.
 *
 * In practice, this is pretty dang fast (~1-2ms for ~5000 style blocks on a
 * 2019 intel MBP) and can save us a good chunk of work (~20ms) during
 * style-change operations which tend to occur much more frequently than
 * style-block creation/deletion, not to mention the pressure taken off the
 * garbage collector.
 */
const getRenamedStyleBlockIds = (
  nextStyleBlocks: ImmutableMap<StyleBlockId, StyleBlock>,
  prevStyleBlocks: ImmutableMap<StyleBlockId, StyleBlock>
): StyleBlockId[] | null => {
  if (nextStyleBlocks === prevStyleBlocks) return [];
  if (prevStyleBlocks.size !== nextStyleBlocks.size) return null;

  const renamedIds: StyleBlockId[] = [];
  for (const [id, block] of prevStyleBlocks) {
    const newBlock = nextStyleBlocks.get(id);
    if (!newBlock) return null;
    if (newBlock === block) continue;
    if (
      newBlock.type !== block.type ||
      newBlock.comb !== block.comb ||
      (newBlock.children !== block.children &&
        !isEqual(newBlock.children, block.children))
    ) {
      return null;
    }
    if (newBlock.name !== block.name) renamedIds.push(id);
  }
  return renamedIds;
};

/**
 * INTERNAL TO `getStyleBlockLookups`
 *
 * The value of `styleBlocks` when `getStyleBlockLookups` last ran.
 */
let lastStyleBlocks: null | ImmutableMap<StyleBlockId, StyleBlock> = null;
/**
 * INTERNAL TO `getStyleBlockLookups`
 *
 * The `slugMap` that `styleBlockLookups` class names were built with.
 */
let lastSlugMap: null | Plugins.PluginsType['slugMap'] = null;
/**
 * INTERNAL TO `getStyleBlockLookups`
 *
 * Cached style block lookup maps.
 *
 * These are from the last `getStyleBlockLookups` run, unless the last run was
 * for an input that was equivalent to `lastStyleBlocks`.
 *
 * See also: `getRenamedStyleBlockIds`
 */
let styleBlockLookups: null | MutableStyleBlockLookups = null;

// Keep in sync with `StyleBlockLookups`: `renameInStyleBlockLookups` must patch
// every map here, or the rename fast path returns stale lookups.
type MutableStyleBlockLookups = {
  fastStyleBlocks: Map<StyleBlockId, FastStyleBlockImpl>;
  styleBlocksByClass: Map<string, Array<FastStyleBlockImpl>>;
  styleBlocksByTag: Map<string, Array<FastStyleBlockImpl>>;
};

/**
 * INTERNAL TO `getStyleBlockLookups`
 *
 * Applies class renames to `lookups` in place, so a rename costs O(renamed)
 * instead of a full O(N) rebuild. Returns `'rebuild'` without mutating anything
 * when the patched lookups could differ from a rebuild, e.g. when the new class
 * name is already taken and match order would change.
 *
 * Returns `'patchedWithSameMatches'` when no old or new name is shared with
 * any other block: the rename then only relabels names that matching compares
 * for equality, so every element matches the same blocks as before.
 */
const renameInStyleBlockLookups = (
  lookups: MutableStyleBlockLookups,
  styleBlocks: ImmutableMap<StyleBlockId, StyleBlock>,
  renamedIds: StyleBlockId[],
  slugMap: Plugins.PluginsType['slugMap']
): 'rebuild' | 'patched' | 'patchedWithSameMatches' => {
  const renames: Array<[FastStyleBlockImpl, string]> = [];
  const newClassNames = new Set<string>();
  let matchesMayChange = false;

  for (const id of renamedIds) {
    const fastStyleBlock = lookups.fastStyleBlocks.get(id);
    const styleBlock = styleBlocks.get(id);
    if (
      !fastStyleBlock ||
      fastStyleBlock.type !== 'class' ||
      styleBlock?.name == null
    ) {
      return 'rebuild';
    }

    const className = toCssClass(styleBlock, slugMap);
    if (
      className == null ||
      lookups.styleBlocksByClass.has(className) ||
      newClassNames.has(className)
    ) {
      return 'rebuild';
    }

    // Matches stay the same only if no other block shares the old name and
    // neither name is a tag name: elements can carry tag block ids, whose tag
    // names are then looked up as class names.
    matchesMayChange ||=
      lookups.styleBlocksByClass.get(fastStyleBlock.name)?.length !== 1 ||
      lookups.styleBlocksByTag.has(fastStyleBlock.name) ||
      lookups.styleBlocksByTag.has(className);

    newClassNames.add(className);
    renames.push([fastStyleBlock, className]);
  }

  for (const [fastStyleBlock, className] of renames) {
    const withOldClassName = (
      lookups.styleBlocksByClass.get(fastStyleBlock.name) ?? []
    ).filter((block) => block !== fastStyleBlock);
    if (withOldClassName.length > 0) {
      lookups.styleBlocksByClass.set(fastStyleBlock.name, withOldClassName);
    } else {
      lookups.styleBlocksByClass.delete(fastStyleBlock.name);
    }

    // Mutating in place keeps every child's `parent` pointer current.
    fastStyleBlock.name = className;
    lookups.styleBlocksByClass.set(className, [fastStyleBlock]);
  }

  return matchesMayChange ? 'patched' : 'patchedWithSameMatches';
};

/**
 * INTERNAL TO `recalculateAffected_fast`
 *
 * See `getStyleBlockLookups` for details.
 *
 * Don't retain this across calls to `getStyleBlockLookups`: a later call may
 * patch these maps and `FastStyleBlock`s in place.
 */
type StyleBlockLookups = Readonly<{
  /** A replacement for `StyleBlockStore.styleBlocks`; see docs for `FastStyleBlock` */
  fastStyleBlocks: Readonly<Map<StyleBlockId, FastStyleBlock>>;
  /** A map of `Array<FastStyleBlock>`, keyed on their CSS-friendly class name */
  styleBlocksByClass: Readonly<Map<string, ReadonlyArray<FastStyleBlock>>>;
  /** A map of `Array<FastStyleBlock>`, keyed on their CSS-friendly tag name */
  styleBlocksByTag: Readonly<Map<string, ReadonlyArray<FastStyleBlock>>>;
}>;

/**
 * INTERNAL TO `recalculateAffected_fast`
 *
 * Generate a few lookup maps that help us quickly determine whether a node is
 * affected by a style block.
 */
const getStyleBlockLookups = (
  styleBlocks: ImmutableMap<StyleBlockId, StyleBlock>,
  slugMap: Plugins.PluginsType['slugMap']
): StyleBlockLookups => {
  // Cached class names carry the prefixes of the `slugMap` they were built with.
  if (lastStyleBlocks && styleBlockLookups && slugMap === lastSlugMap) {
    const renamedIds = getRenamedStyleBlockIds(styleBlocks, lastStyleBlocks);
    if (renamedIds?.length === 0) {
      lastStyleBlocks = styleBlocks;
      return styleBlockLookups;
    }
    const outcome = renamedIds
      ? renameInStyleBlockLookups(
          styleBlockLookups,
          styleBlocks,
          renamedIds,
          slugMap
        )
      : 'rebuild';
    if (outcome !== 'rebuild') {
      // A new identity keeps `recalculateAffected_fast` from reusing results
      // memoized against the old names.
      if (outcome === 'patched') styleBlockLookups = {...styleBlockLookups};
      lastStyleBlocks = styleBlocks;
      return styleBlockLookups;
    }
  }

  const styleBlocksByClass = new Map<string, Array<FastStyleBlockImpl>>();
  const styleBlocksByTag = new Map<string, Array<FastStyleBlockImpl>>();
  const styleBlockParents = new Map<StyleBlockId, FastStyleBlockImpl>();
  const fastStyleBlocks = new Map<StyleBlockId, FastStyleBlockImpl>();

  for (const [id, styleBlock] of styleBlocks) {
    const {type, children, comb, name} = styleBlock;

    let fastStyleBlock;
    if (
      type === 'class' ||
      // TODO(lou): We can probably track element blocks directly which should
      // improve performance, but for now trying to keep our existing logic as-is
      // by coding element styles as classes.
      //
      // Tracking: https://webflow.atlassian.net/browse/STYL-2047
      type === 'element'
    ) {
      // A `null` name for a `class` style block is possible, but we don't
      // enforce that at the type level, so we simply ignore this case as it
      // will also be ignored by the style system:
      if (name == null) continue;

      // Here's where we pre-compute our css-friendly class name:
      const className = toCssClass(styleBlock, slugMap);

      // `cssifyName` can return `null` for cases where we've supplied an
      // invalid human-readable class name (i.e. an empty string).
      //
      // This edge-case ultimately will be ignored by the style system, so we
      // can ignore it too:
      if (className == null) continue;

      fastStyleBlock = new FastStyleBlockImpl(id, type, className, comb);

      // We use the CSS class name to match style blocks to our nodes later, so
      // we use the `styleBlocksByClass` map here to achieve that:
      let withThisClassName = styleBlocksByClass.get(className);
      if (!withThisClassName) {
        withThisClassName = [];
        styleBlocksByClass.set(className, withThisClassName);
      }
      withThisClassName.push(fastStyleBlock);
    } else if (type === 'tag') {
      // We do not currently support combo-class + tag selectors, so we can
      // ignore those:
      if (comb === '&' || name == null) continue;

      // Note: In the case of a `tag` style block, `name` refers to the tag
      // name, not the class name.
      fastStyleBlock = new FastStyleBlockImpl(id, type, name, comb);

      let withThisTag = styleBlocksByTag.get(name);
      if (!withThisTag) {
        withThisTag = [];
        styleBlocksByTag.set(name, withThisTag);
      }
      withThisTag.push(fastStyleBlock);
    }

    if (!fastStyleBlock) continue;

    // Now populate our `styleBlockParents` map based on our `children` style
    // block ID list:
    for (const childId of children) {
      styleBlockParents.set(childId, fastStyleBlock);
    }

    fastStyleBlocks.set(id, fastStyleBlock);
  }

  // One last post-processing step: Using our `styleBlockParents` map, write the
  // `.parent` entries to the applicable `FastStyleBlock`s
  for (const styleBlock of fastStyleBlocks.values()) {
    const parent = styleBlockParents.get(styleBlock.id);
    if (parent) styleBlock.parent = parent;
  }

  styleBlockLookups = {
    styleBlocksByClass,
    styleBlocksByTag,
    fastStyleBlocks,
  };

  lastStyleBlocks = styleBlocks;
  lastSlugMap = slugMap;

  return styleBlockLookups;
};

/**
 * Subroutine for `recalculateAffected_fast` & friends.
 *
 * IMPORTANT:
 *
 * This function should never be exported as it intentionally leaks
 * implementation details for performance reasons.
 *
 * Specifically, the `applies_mut` parameter is mutated as part of this
 * subroutine.
 *
 * TL;DR:
 *
 * - If you use this function, be careful to only pass in an `applies_mut` set
 *   that you "own" as it will be mutated.
 * - After the subroutine completes, ownership goes back to the caller (i.e. we
 *   do not keep it around and mutate it as a side effect) so it's safe to use
 *   `applies_mut` after the call.
 */
const _addStyleBlocksForMatchesByName = (
  matchesByName: readonly FastStyleBlock[],
  applies_mut: Set<string>,
  fastStyleBlock: Readonly<Map<string, FastStyleBlock>>,
  styleBlockIds: Iterable<string>
) => {
  /**
   * Simple cache of a set of computed class names applied to our node.
   *
   * Using manual cache rather than memoizing here since it's less of a
   * memory hog and faster than a memo lookup.
   */
  let classNameSet_;

  // For every style block that has the same class name as the style block
  // from `styleBlockIds`...
  for (const classBlock of matchesByName) {
    // Simple case; direct/global class selector of a class this node has:
    if (classBlock.comb === '') {
      applies_mut.add(classBlock.id);
    } else if (classBlock.comb === '&') {
      // More complex case: Here we need to verify that all parent blocks
      // apply before we can say that this combo block applies.
      //
      // This is because we treat combo classes as multiple blocks.
      //
      // For instance, the selector `.foo.bar.baz` is represented like so:
      //
      // ```
      // [
      //   {
      //     _id: 'foo-id',
      //     name: 'foo',
      //     comb: '',
      //     children: ['bar-id'],
      //   },
      //   {
      //     _id: 'bar-id',
      //     name: 'bar',
      //     comb: '&',
      //     children: ['baz-id'],
      //   },
      //   {
      //     _id: 'baz-id',
      //     name: 'baz',
      //     comb: '&',
      //     children: [],
      //   },
      // ]
      // ```
      //
      // So here we need to first verify that `baz` is applied to our node,
      // then `bar`, and finally `foo`. If all of these apply, then the
      // "deepest" style block applies (and so do each ancestor style block)

      let depth = 0;
      let applicableParentBlocks;
      // We assume that the combo class block applies, even if we can't find
      // a matching parent (i.e. it's an "orphan" style block)
      //
      // We still need to consider orphan style blocks as applicable because
      // we still render them in our CSS output, without any parent class as
      // part of their selector.
      //
      // This is actually a bug with our CSS renderer, but we need to
      // reflect that behavior when determining whether style blocks apply
      // to match what ultimately happens in the browser.
      let classBlockMatches = true;
      let parentBlock = classBlock.parent;
      while (parentBlock) {
        const {comb} = parentBlock;
        if (parentBlock.type !== 'class' || comb === '|') {
          classBlockMatches = false;
          break;
        }
        // Aside: I know this is a little ugly, but this is a very tight
        // loop and we want to avoid memory pressure and cache lookups that
        // come with a memoized function, so we "inline" this behavior _at
        // the last moment_ so we can avoid any overhead until we know we
        // definitely need it.
        const classNameSet = (classNameSet_ =
          classNameSet_ || getClassNameSet(fastStyleBlock, styleBlockIds));

        if (classNameSet.has(parentBlock.name)) {
          if (!applicableParentBlocks)
            applicableParentBlocks = new Set<FastStyleBlock>();

          // If our element's class names contain the parent block's class
          // name, we can treat that block as "applicable" and continue...
          applicableParentBlocks.add(parentBlock);
        } else {
          // ...otherwise we've reached a parent style block whose class is
          // not part of the node's class names, and thus this set of style
          // blocks do not apply
          classBlockMatches = false;
          break;
        }

        if (comb !== '&') {
          // If the current `parentBlock` isn't a combo class, we exit the
          // loop since we have nothing else to check
          break;
        }

        // Otherwise, continue up the style block tree...
        parentBlock = parentBlock.parent;

        // Guard against possible infinite loops.
        //
        // This could happen if a cycle somehow occurs in our style blocks.
        if (++depth > 1000) {
          reportBug(
            'RecalculateAffectedStyleBlockMaxDepthExceeded',
            `A style block tree exceeds a max depth; is there a cycle in the data structure?`,
            {
              team: 'dxp',
              ticket: 'https://webflow.atlassian.net/browse/DESXP-6117',
            }
          );
          break;
        }
      }

      // After the loop that walked up the style block tree, we can now
      // check whether the style blocks we walked all apply to our node...

      if (!classBlockMatches) {
        // ...if they don't, exit the outer loop and move on to the next
        // style block in our `matchesByName` set:
        continue;
      }

      // ...otherwise, we know the current `classBlock` applies, so we add
      // our node to the result map:
      applies_mut.add(classBlock.id);

      // ...and finally we can do the same for every style block we walked
      // above that we stored in `applicableParentBlocks`:
      if (applicableParentBlocks) {
        for (const appliedBlock of applicableParentBlocks) {
          applies_mut.add(appliedBlock.id);
        }
      }
    }
  }
};

/**
 * Subroutine for `recalculateAffected_fast` & friends.
 *
 * IMPORTANT:
 *
 * This function should never be exported as it intentionally leaks
 * implementation details for performance reasons.
 *
 * Specifically, the `applies_mut` parameter is mutated as part of this
 * subroutine.
 *
 * TL;DR:
 *
 * - If you use this function, be careful to only pass in an `applies_mut` set
 *   that you "own" as it will be mutated.
 * - After the subroutine completes, ownership goes back to the caller (i.e. we
 *   do not keep it around and mutate it as a side effect) so it's safe to use
 *   `applies_mut` after the call.
 */
function _addStyleBlocksForMatchesByTag<A>(
  matchesByTag: readonly FastStyleBlock[],
  applies_mut: Set<string>,
  ancestors: A[],
  fastStyleBlocks: Readonly<Map<string, FastStyleBlock>>,
  getStyleBlockIdsForAncestor: (ancestor: A) => Iterable<string>
) {
  for (const tagBlock of matchesByTag) {
    // Simple case; global tag selector matching this node's tag:
    if (tagBlock.comb === '') {
      applies_mut.add(tagBlock.id);
      continue;
    }

    // Besides global tag selectors, we only tag selectors as descendants
    // ("|"), not combinators ("&"):
    if (tagBlock.comb !== '|') {
      continue;
    }

    // So now we know we're dealing with a descendant selector, which means
    // we need to walk back up our tree until we find an ancestor that
    // matches our parent selectors.
    //
    // The only kinds of selectors that we need to worry about in this
    // context are tag descendants of a (combo-)class.
    //
    // Here are some examples that fit this criteria:
    //
    // - '.foo p'
    // - '.foo.bar p'
    // - '.foo.bar.biz p'
    //
    // However the following cases are _not_ possible with the Designer as
    // of today, so we ignore them:
    //
    // - Descending from other tags (e.g. `section p`)
    // - Descending from tag-class combos (e.g. `section.foo p`)
    // - More than one level of ancestry (`.foo .bar p`)

    // We iterate backwards through `ancestors` to walk "up" the tree,
    // stopping at the first node that matches our descendant sub-selector
    // (e.g. `.foo.bar`):
    for (let i = ancestors.length - 1; i >= 0; --i) {
      const ancestor = ancestors[i];

      if (!ancestor) continue;

      // Note: The following `while` loop closely resembles that from the
      // combo class portion fo the `matchesByName` loop above, but they
      // differ enough that abstraction may make the code harder to
      // optimize.
      //
      // If you're reading this and need to add some new functionality that
      // applies in both cases, it may be a good time to pull this similar
      // logic into a function.

      /**
       * Simple cache of a set of computed class names applied to our
       * node.
       *
       * Using manual cache rather than memoizing here since it's less of
       * a memory hog and faster than a memo lookup.
       */
      let classNameSet_;
      let depth = 0;
      let parentBlock = tagBlock.parent;
      let ancestorMatches = true;
      while (parentBlock) {
        const {comb} = parentBlock;
        if (
          // We don't support tag parent style blocks (e.g. `section p`)...
          parentBlock.type !== 'class' ||
          // ...nor do we support >1 ancestor, (e.g. `.foo .bar p`)
          comb === '|'
        ) {
          ancestorMatches = false;
          break;
        }

        const classNameSet = (classNameSet_ =
          classNameSet_ ||
          getClassNameSet(
            fastStyleBlocks,
            getStyleBlockIdsForAncestor(ancestor)
          ));

        if (!classNameSet.has(parentBlock.name)) {
          ancestorMatches = false;
          break;
        }

        if (comb !== '&') {
          // If the current `parentBlock` isn't a combo class, we exit the
          // loop since we have nothing else to check
          break;
        }

        // Otherwise, continue up the style block tree...
        parentBlock = parentBlock.parent;

        // Guard against possible infinite loops.
        //
        // This could happen if a cycle somehow occurs in our style
        // blocks.
        if (++depth > 1000) {
          reportBug(
            'RecalculateAffectedStyleBlockMaxDepthExceeded',
            `A style block tree exceeds a max depth; is there a cycle in the data structure?`,
            {
              team: 'dxp',
              ticket: 'https://webflow.atlassian.net/browse/DESXP-6117',
            }
          );
          break;
        }
      }

      // If this ancestor matches the ancestor selector, then this node
      // matches the main descendant selector we started with:
      if (ancestorMatches) {
        applies_mut.add(tagBlock.id);
        break;
      }
    }
  }
}

/**
 * Adds all style blocks that apply to the given element to the `applies_mut` set.
 *
 * This is a subroutine for `recalculateAffected_fast`. The `applies_mut` parameter
 * is intentionally mutated for performance reasons.
 */
const _addStyleBlocksAppliedToElement = (
  {fastStyleBlocks, styleBlocksByClass, styleBlocksByTag}: StyleBlockLookups,
  element: ExpressionElement,
  ancestors: ExpressionElement[],
  applies_mut: Set<StyleBlockId>
): void => {
  const styleBlockIds = getStyleBlockIds(element);

  // First, look up all possible matching style blocks based on the class
  // names that are explicitly applied to this node via `styleBlockIds`
  for (const styleBlockId of styleBlockIds) {
    const fastStyleBlock = fastStyleBlocks.get(styleBlockId);

    // We _shouldn't_ encounter any non-class (i.e. tag) style blocks in here,
    // but if we do, we still treat them as classes, as our renderer treats
    // them as classes when outputting `className` in the HTML.
    //
    // So if your element somehow has `default-h2` in its `styleBlockIds`, it
    // will have `h2` as a class name.
    if (!fastStyleBlock) continue;

    // Get all style blocks that have the same computed class name:
    const matchesByName = styleBlocksByClass.get(fastStyleBlock.name);
    if (!matchesByName) continue;

    _addStyleBlocksForMatchesByName(
      matchesByName,
      applies_mut,
      fastStyleBlocks,
      styleBlockIds
    );
  }

  // Now, find any style blocks that matches our element based on its tag...
  const matchesByTag = styleBlocksByTag.get(getElementTag(element));
  if (matchesByTag) {
    _addStyleBlocksForMatchesByTag(
      matchesByTag,
      applies_mut,
      ancestors,
      fastStyleBlocks,
      (ancestor) => getStyleBlockIds(ancestor)
    );
  }
};

/**
 * Returns the HTML tag name for a given `ExpressionElement` type, or `div` if none is found.
 */
const getElementTag = weakMemo((element: ExpressionElement) => {
  // Text nodes shouldn't be mapped to a tag
  if (isStringElement(element)) {
    return '';
  }

  /**
   * Not all `ExpressionElements` include a tag name in `data.tag`, and since the result
   * of this function is used to look up matching Style Blocks by the exact tag name, it's
   * not safe to assume that the default 'div' returned by `getTag` can be used here.
   *
   * `TAG_BY_NODE_TYPE` contains a mapping of node types (element name) to either the string value
   * to use as the tag name, or an object denoting that the tag should be looked up from `data.tag`.
   */
  const tagByNodeType = TAG_BY_NODE_TYPE[getName(element.type)];

  // If `tagByNodeType` is a string, it represents the tag name matching this element type.
  if (typeof tagByNodeType === 'string') {
    return tagByNodeType;
  }

  // Otherwise, look up the tag name on `element.data.tag`, returning 'div' if undefined.
  return getTagFromData(element);
});

/**
 * Core implementation of `recalculateAffected`.
 *
 * This implementation takes advantage of the fact that every element has a
 * `styleBlockIds` array. From this, we determine what classes we have, and then
 * use a map to look up _all_ style blocks that could apply. We do something
 * similar for tags as well.
 *
 * The pseudocode looks like this:
 *
 * ```
 * for styleBlock in styleBlocks
 *   ...
 *
 * for element in elements
 *   ...
 * ```
 *
 * Which is O(N + M), much better than a naive O(N * M) nested loop approach.
 *
 * This approach is inspired by how browsers themselves match style rules to
 * elements in the DOM. We can make a _ton_ of assumptions browser vendors can't
 * however, so we aren't doing anything particularly fancy here with things like
 * bloom filters as they generally won't make a meaningful difference.
 *
 * We may find that in the future these use-cases _do_ become possible or
 * common, in which case we probably _should_ employ more of the same tricks
 * browsers use.
 *
 * If you'd like to learn more about how browsers do this so fast, the talk "CSS
 * runtime performance" given by Nolan Lawson at the performance.now 2022
 * conference is a fun introduction: https://youtu.be/nWcexTnvIKI
 */
const recalculateAffected_fast = lruMemoize(
  (
    componentMap: Component.ComponentMap,
    plugins: Plugins.PluginsType,
    styleBlockLookups_: StyleBlockLookups
  ): AffectedNodesByStyleBlockId => {
    const result: Partial<Record<StyleBlockId, string[]>> = {};

    /**
     * A stack of ancestor elements; think of this almost like a "call stack" that
     * led us to the element that we're currently processing inside `reduceTree`.
     *
     * We use this stack to look up ancestor elements when matching against
     * descendant selectors (e.g. `.my-rich-text-el p`).
     *
     * We manually push/pop our element off the `ancestors` stack at the bottom of
     * the `reduceTree` function, where the recursion happens.
     */
    const ancestors: Array<ExpressionElement> = [];

    /**
     * A set that contains all the style blocks that apply to the element being
     * visited in the `reduceTree` function.
     *
     * Note: We get better performance by using a single set instance and clearing
     * it after each element-visit in our `reduceTree` function, so that's why we're
     * defining it outside `reduceTree`.
     */
    const applies_mut = new Set<string>();

    /**
     * Simple recursive walk function.
     *
     * For every element in the tree, we look up _possibly_-applicable style blocks,
     * and for any that we confirm _do_ apply, we push our element id to the style
     * block's affects list.
     */
    const reduceTree = (element: ExpressionElement) => {
      _addStyleBlocksAppliedToElement(
        styleBlockLookups_,
        element,
        ancestors,
        applies_mut
      );

      // Finally, update our result by pushing our element ID to the applicable style
      // block -> element ID lists in our result:
      for (const styleBlockId of applies_mut) {
        (result[styleBlockId] ||= []).push(element.id);
      }

      // Reset the `applies` set before moving on to the next element
      applies_mut.clear();

      // Check if we have descendants to recurse into
      const descendants = getDescendants(element);
      if (descendants.length === 0) return;

      // Keep track of our current element in the ancestors stack
      ancestors.push(element);

      // Recurse into descendants
      descendants.forEach((child) => {
        reduceTree(child);
      });

      // Remove the current element from the ancestors stack
      ancestors.pop();
    };

    // Note: This is _not_ unique to the fast implementation, but we don't catch
    // descendant selectors whose ancestor wraps the user-component instance,
    // simply since we don't recurse into user-component instances. In other
    // words, we treat symbols instances as opaque. In practice this doesn't
    // matter today since descendant selectors are currently only supported on
    // rich text blocks and the element types you can nest within them (which
    // doesn't include user-component instances).
    const roots = componentMap.reduce<ExpressionElement[]>((acc, component) => {
      const render = Component.getRender(component);
      if (
        Expressions.isElement(render) &&
        !Plugins.isPluginComponent(plugins, component)
      ) {
        const element = Expressions.getElement(render);
        if (element) acc.push(element);
      }

      return acc;
    }, []);

    for (const root of roots) {
      reduceTree(root);
    }

    return result;
  }
);

export type AffectedNodesByStyleBlockId = Readonly<
  Partial<Record<StyleBlockId, ReadonlyArray<ElementId>>>
>;

/**
 * Calculates which elements are affected by each style block.
 *
 * Returns a map of StyleBlockId -> ElementId[] showing which elements
 * each style block's CSS selector matches.
 */
export const recalculateAffected = ({
  componentMap,
  plugins,
  styleBlocks,
  slugMap,
}: {
  componentMap: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  styleBlocks: StyleBlockStateType['styleBlocks'];
  slugMap: Plugins.PluginsType['slugMap'];
}): AffectedNodesByStyleBlockId => {
  return recalculateAffected_fast(
    componentMap,
    plugins,
    getStyleBlockLookups(styleBlocks, slugMap)
  );
};

/**
 * Returns a human-readable array of style block to element mappings.
 * Useful for debugging which elements are affected by which style blocks.
 */
export function getHumanizedStyleMapData({
  styleMap,
  styleBlockState,
  slugMap,
  variantDataMap,
}: {
  styleMap: AffectedNodesByStyleBlockId;
  styleBlockState: StyleBlockStateType;
  slugMap: Plugins.PluginsType['slugMap'];
  variantDataMap: VariantDataMap;
}): {
  blockId: string;
  selector: string;
  nodes: string;
}[] {
  const entries: Array<{
    blockId: string;
    nodes: string;
    selector: string;
  }> = [];

  Object.keys(styleMap).forEach((blockId) => {
    const nodesList = styleMap[blockId];
    const styleBlock = styleBlockState.getBlock(blockId, {
      decorate: true,
      slugMap,
      variantDataMap,
    });
    if (styleBlock) {
      entries.push({
        blockId,
        // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string'.
        selector: styleBlock.getIn(['selector', 'text']),
        // @ts-expect-error - TS18048 - 'nodesList' is possibly 'undefined'.
        nodes: nodesList.join(', '),
      });
    }
  });

  return entries;
}
